/**
 * Shared client for GET /api/search-suggestions (TEC navbar redesign Phase 4 item 2): the same
 * route the static launcher's vanilla-JS island fetches (web-static/pages/landing/
 * landing-nav-live-index-script.ts) and the React fallback launcher (FallbackLauncher in
 * SearchLauncher.tsx, used on MinimalChrome pages that mount no SearchProvider/FacetsContext).
 * Both call the exact same server route (server/routes/search-suggestions.ts), so the two
 * dropdowns can never disagree.
 */

export interface NavSearchSuggestion {
  label: string;
  attribute: string;
  attributeLabel: string;
  value: string;
  count: number;
  href: string;
  /** Hosted logo URL, only on `companyId` rows (see server/utils/nav-search-suggestions.ts). */
  logoUrl?: string;
}

export interface NavSearchSuggestionsResponseBody {
  suggestions: NavSearchSuggestion[];
}

export type NavSearchSuggestionsLocale = 'en' | 'fi' | 'sv';
export type NavSearchSuggestionsLocationSystem = 'legacy' | 'new';

export function buildSearchSuggestionsUrl(
  locale: NavSearchSuggestionsLocale,
  q: string,
  locationSystem?: NavSearchSuggestionsLocationSystem,
): string {
  const params = new URLSearchParams({ locale, q });
  if (locationSystem) params.set('ls', locationSystem);
  return `/api/search-suggestions?${params.toString()}`;
}

/** Never throws: a network failure or non-2xx response resolves to an empty list, same as the
 * menu-pulse client hook's own fail-quiet behaviour (components/navigation/menu/useMenuPulse.ts). */
export async function fetchNavSearchSuggestions(
  locale: NavSearchSuggestionsLocale,
  q: string,
  locationSystem?: NavSearchSuggestionsLocationSystem,
): Promise<NavSearchSuggestion[]> {
  try {
    const res = await fetch(buildSearchSuggestionsUrl(locale, q, locationSystem), {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return [];
    const body = (await res.json()) as NavSearchSuggestionsResponseBody;
    return Array.isArray(body.suggestions) ? body.suggestions : [];
  } catch {
    return [];
  }
}
